import { useController } from "react-hook-form";
import type { Lens } from "@hookform/lenses";
import type { PresetData, SizeQuantity } from "../../model/transferLabTypes";
import { CollapsableForm } from "./CollapsableForm";
import { PresetSizeCard } from "./PresetSizeCard";

export type PresetFormProps = {
  presetId: string;
  presetLens: Lens<PresetData>;
  onRemove: () => void;
  artworkId?: string;
  transferTypeId?: string;
  enhancementTier?: string;
};

export function PresetForm({
  presetId,
  presetLens,
  onRemove,
  artworkId,
  transferTypeId,
  enhancementTier
}: PresetFormProps) {
  // Get preset data using controller with lens
  const { field } = useController(presetLens.interop());

  const preset = field.value || {};
  const { sizes } = preset;

  // Calculate total quantity
  const totalQuantity = sizes.reduce(
    (sum: number, size: SizeQuantity) => sum + (size.quantity || 0),
    0
  );

  const handleQuantityChange = (sizeId: string, quantity: number) => {
    field.onChange({
      ...preset,
      sizes: sizes.map((size: SizeQuantity) => (size.id === sizeId ? { ...size, quantity } : size))
    });
  };

  return (
    <CollapsableForm title={preset.name} onRemove={onRemove} id={`preset-${presetId}`}>
      <div className="space-y-4">
        {/* Size cards in a row */}
        <div className="flex gap-3 overflow-x-auto pb-4">
          {sizes.map((size: SizeQuantity, index: number) => (
            <PresetSizeCard
              key={size.id}
              presetId={presetId}
              size={size}
              quantityLens={presetLens.focus(`sizes.${index}.quantity`)}
              onChange={quantity => handleQuantityChange(size.id, quantity)}
              {...(artworkId && { artworkId })}
              {...(transferTypeId && { transferTypeId })}
              {...(enhancementTier && { enhancementTier })}
            />
          ))}
        </div>

        {/* Total quantity summary */}
        {totalQuantity > 0 && (
          <div className="flex items-center justify-between rounded-lg bg-blue-50 p-3">
            <span className="text-sm font-medium text-gray-700">Total Quantity:</span>
            <span className="text-sm font-bold text-blue-700">{totalQuantity}</span>
          </div>
        )}
      </div>
    </CollapsableForm>
  );
}
